import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  Category,
  DiscountAndFee,
  PricebookSyncManifest,
  Product,
  ProductFilters,
  ProductLookups,
  ProductModifiers,
} from './products.models';
import { initState, productsReducer, ProductsState } from './products.reducer';

type AvailableItem = {
  name: string;
  count: number;
};

type LookupOptions = {
  businessUnitIds?: string[];
};

type FilterOptions = {
  reset: boolean;
};

interface OfflineProductsContextProps {
  setProducts: (
    filterOptions: Partial<ProductFilters>,
    fetchFn: () => Promise<Product[]>
  ) => Promise<void>;
  setLookups: (fetchFn: () => Promise<ProductLookups>) => Promise<void>;
  syncOfflinePricebook: (options: { force?: boolean }) => Promise<unknown>;
  pricebookSync: { error?: string | null; lastSyncTime?: string };
  lookupsSync: { error?: string | null; lastSyncTime?: string };
  modifiersSync: { error?: string | null; lastSyncTime?: string };
}

interface ProductsContextProps extends OfflineProductsContextProps {
  products: Product[];
  productsFilter: ProductFilters;
  availableBrands: AvailableItem[];
  availableCategories: Category[];
  availableDiscounts: DiscountAndFee[];
  availableFees: DiscountAndFee[];
  availableRebates: DiscountAndFee[];
  availableProductTypes: AvailableItem[];
  isLoading: boolean;
  isLoaded: boolean;
  isLoadedLookups: boolean;
  isLoadingLookups: boolean;
  error: string | null;
  lookupsError: string | null;
  lookupsSetError: string | null;
  fetchProduct: (
    req: Pick<Product, 'pricebookId' | 'type'>,
    options?: { signal: AbortSignal }
  ) => Promise<void>;
  fetchProducts: (
    filterOptions: Partial<ProductFilters>,
    options?: { signal: AbortSignal }
  ) => Promise<Product[]>;
  fetchLookups: (
    params: LookupOptions,
    options?: { signal: AbortSignal }
  ) => Promise<ProductLookups>;
  fetchModifiers: (options?: {
    signal: AbortSignal;
  }) => Promise<ProductModifiers>;
  fetchPricebookSyncManifest: (options?: {
    signal: AbortSignal;
  }) => Promise<PricebookSyncManifest>;
  updateProductsFilter: (
    filterOptions: Partial<ProductFilters>,
    options?: FilterOptions
  ) => void;
}

export const ProductsContext = createContext<ProductsContextProps | undefined>(
  undefined
);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProductsState>;
}>;

export const ProductsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(productsReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  const products = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const productsSorted = useMemo(
    () =>
      products.sort((a, b) => {
        const priceA =
          a.prices.find((price) => price.priceType === a.defaultPriceType)
            ?.value || 0;
        const priceB =
          b.prices.find((price) => price.priceType === b.defaultPriceType)
            ?.value || 0;

        const priceSort =
          state.filterOptions.sortOrder === 'asc'
            ? priceA - priceB
            : priceB - priceA;
        const matchesCertifications =
          Number(b.matchesCertifications) - Number(a.matchesCertifications);

        return matchesCertifications || priceSort;
      }),
    [products, state.filterOptions.sortOrder]
  );

  const availableProductTypes = useMemo(
    () =>
      ['EQUIPMENT', 'SERVICE', 'MATERIAL']
        .map((name) => ({
          name,
          count: products.filter((product) => product.type === name).length,
        }))
        .sort((a, b) => b.count - a.count),
    [products]
  );

  const availableBrands = useMemo(
    () => state.lookups.brands.sort((a, b) => a.name.localeCompare(b.name)),
    [state.lookups.brands]
  );

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const fetchProducts: ProductsContextProps['fetchProducts'] = useCallback(
    async (filterOptions, options) => {
      dispatch({ type: 'FETCH_PRODUCTS', payload: { filterOptions } });

      try {
        const products = await httpClient.get<Product[]>('/pricebook/query', {
          params: filterOptions,
          signal: options?.signal,
        });
        dispatch({
          type: 'FETCH_PRODUCTS_SUCCESS',
          payload: { products },
        });

        return products;
      } catch (error) {
        if (options?.signal?.aborted) {
          dispatch({ type: 'FETCH_PRODUCTS_CANCELLED' });
          return [];
        }

        dispatch({
          type: 'FETCH_PRODUCTS_FAILURE',
          payload: { error: `${error}` },
        });
        throw error;
      }
    },
    [httpClient]
  );

  const fetchProduct: ProductsContextProps['fetchProduct'] = useCallback(
    async (req, options) => {
      dispatch({
        type: 'FETCH_PRODUCT',
        payload: { pricebookId: req.pricebookId },
      });

      try {
        const product = await httpClient.get<Product>(
          `/pricebook/${req.pricebookId}`,
          {
            params: { entityType: req.type },
            signal: options?.signal,
          }
        );
        dispatch({
          type: 'FETCH_PRODUCT_SUCCESS',
          payload: { product },
        });
      } catch (error) {
        if (options?.signal?.aborted) {
          dispatch({ type: 'FETCH_PRODUCTS_CANCELLED' });
          return;
        }

        dispatch({
          type: 'FETCH_PRODUCTS_FAILURE',
          payload: { error: `${error}` },
        });
        throw error;
      }
    },
    [httpClient]
  );

  const fetchLookups: ProductsContextProps['fetchLookups'] = useCallback(
    async (params, options) => {
      dispatch({ type: 'FETCH_PRODUCT_LOOKUPS' });

      try {
        const lookups = await httpClient.get<ProductLookups>(
          '/pricebook/lookups',
          { params, signal: options?.signal }
        );
        dispatch({
          type: 'FETCH_PRODUCT_LOOKUPS_SUCCESS',
          payload: { lookups },
        });
        return lookups;
      } catch (error) {
        if (!options?.signal?.aborted) {
          dispatch({
            type: 'FETCH_PRODUCT_LOOKUPS_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
        return {
          categories: [],
          brands: [],
          discounts: [],
          fees: [],
          rebates: [],
        };
      }
    },
    [httpClient]
  );

  const fetchModifiers: ProductsContextProps['fetchModifiers'] = useCallback(
    async (options) => {
      dispatch({ type: 'FETCH_PRODUCT_MODIFIERS' });

      try {
        const modifiers = await httpClient.get<ProductModifiers>(
          '/pricebook/modifiers',
          { signal: options?.signal }
        );
        dispatch({
          type: 'FETCH_PRODUCT_MODIFIERS_SUCCESS',
          payload: { modifiers },
        });
        return modifiers;
      } catch (error) {
        if (!options?.signal?.aborted) {
          dispatch({
            type: 'FETCH_PRODUCT_MODIFIERS_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
        return {
          discounts: [],
          fees: [],
          rebates: [],
        };
      }
    },
    [httpClient]
  );

  const fetchPricebookSyncManifest: ProductsContextProps['fetchPricebookSyncManifest'] =
    useCallback(
      (options) =>
        httpClient.get<PricebookSyncManifest>('/pricebook/sync', {
          signal: options?.signal,
        }),
      [httpClient]
    );

  const updateProductsFilter = useCallback(
    (filterOptions: Partial<ProductFilters>, options?: FilterOptions) =>
      dispatch({
        type: 'UPDATE_FILTER_OPTIONS',
        payload: { filterOptions, reset: options?.reset },
      }),
    []
  );

  const setProducts: ProductsContextProps['setProducts'] = useCallback(
    async (filterOptions, fetchFn) => {
      dispatch({ type: 'FETCH_PRODUCTS', payload: { filterOptions } });

      try {
        const products = await fetchFn();

        dispatch({
          type: 'FETCH_PRODUCTS_SUCCESS',
          payload: { products },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_PRODUCTS_FAILURE',
          payload: { error: `${error}` },
        });
        throw error;
      }
    },
    []
  );

  const setLookups: ProductsContextProps['setLookups'] = useCallback(
    async (fetchFn: () => Promise<ProductLookups>) => {
      dispatch({ type: 'SET_PRODUCT_LOOKUPS' });

      try {
        const lookups = await fetchFn();

        dispatch({
          type: 'SET_PRODUCT_LOOKUPS_SUCCESS',
          payload: { lookups },
        });
      } catch (error) {
        dispatch({
          type: 'SET_PRODUCT_LOOKUPS_FAILURE',
          payload: { error: `${error}` },
        });
        throw error;
      }
    },
    []
  );

  return (
    <ProductsContext.Provider
      value={{
        fetchProducts,
        fetchProduct,
        fetchLookups,
        fetchModifiers,
        fetchPricebookSyncManifest,
        updateProductsFilter,
        setProducts,
        setLookups,
        syncOfflinePricebook: () => Promise.resolve(void 0),
        products: productsSorted,
        availableCategories: state.lookups.categories,
        availableDiscounts: state.lookups.discounts,
        availableFees: state.lookups.fees,
        availableRebates: state.lookups.rebates,
        availableProductTypes,
        availableBrands,
        productsFilter: state.filterOptions || {},
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isLoadedLookups: state.isLoadedLookups,
        isLoadingLookups: state.isLoadingLookups,
        error: state.error,
        lookupsError: state.lookupsError,
        lookupsSetError: state.lookupsSetError,
        lookupsSync: {},
        pricebookSync: {},
        modifiersSync: {},
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within an ProductsProvider');
  }
  return context;
};
